import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatMessage } from '@mise/shared';

interface ChatState {
  isOpen: boolean;
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  inputValue: string;

  // Actions
  toggleOpen: () => void;
  setOpen: (open: boolean) => void;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  clearMessages: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInputValue: (value: string) => void;
}

const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      isOpen: false,
      messages: [],
      isLoading: false,
      error: null,
      inputValue: '',

      toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),

      setOpen: (open) => set({ isOpen: open }),

      setInputValue: (value) => set({ inputValue: value }),

      addMessage: (message) => {
        const id = generateId();
        set((state) => ({
          messages: [
            ...state.messages,
            {
              id,
              text: message.text,
              sender: message.sender,
              timestamp: Date.now(),
              status: message.status || 'sent',
            },
          ],
        }));
        return id;
      },

      updateMessage: (id, updates) =>
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === id ? { ...msg, ...updates } : msg
          ),
        })),

      clearMessages: () => set({ messages: [], error: null }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),
    }),
    {
      name: 'mise-dashboard-chat',
      partialize: (state) => ({
        messages: state.messages.slice(-50), // Keep last 50 messages
      }),
    }
  )
);
